import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import { FormHelperText, Typography } from '@material-ui/core'
import FormTextField from '../form/FormTextField'
import FormSelect from '../form/FormSelect'
import FormDateTimeField from '../form/FormDateTimeField'
import FormImageField from '../form/FormImageField'
import validation, { tenantUnavailable } from './AdvertismentFormValidation'
import { placements } from '../../models/advertisements/AdvertisementModel'

function tenantOptions (tenants, selectedIds = []) {
  const options = tenants.map(tenant => {
    const unavailable = tenantUnavailable(tenant)
    const name = tenant.name || tenant.id

    return {
      value: tenant.id,
      name: unavailable ? `${name} (nieaktywny lub nieważna licencja)` : name,
      disabled: unavailable && !selectedIds.includes(tenant.id)
    }
  })
  const missingOptions = selectedIds
    .filter(id => !tenants.some(tenant => tenant.id === id))
    .map(id => ({ value: id, name: `${id} (niedostępny - usuń z wyboru)` }))

  return Object.assign({}, ...options.concat(missingOptions).map(option => ({ [option.value]: option })))
}

export function AdvertisementForm({
  handleSubmit,
  busy,
  tenants,
  initialValues,
  currentImage,
  selectedPlacements = []
}) {
  const showMobileStyleConfig = selectedPlacements.includes('mobileMainPanel') || selectedPlacements.includes('mobileSecondaryPanel')
  const showWebStyleConfig = selectedPlacements.includes('webMainPanel') || selectedPlacements.includes('webSecondaryPanel')

  return (
    <form onSubmit={handleSubmit}>
      <div>
        <Field
          name='name'
          component={FormTextField}
          label='Nazwa'
          inputProps={{ maxLength: 200 }}
        />
        <Field
          name='description'
          component={FormTextField}
          label='Opis'
          multiline
          rows={4}
          inputProps={{ maxLength: 1000 }}
        />
        <Field
          name='targetUrl'
          component={FormTextField}
          label='Adres docelowy (URL)'
        />
        <Field
          name='order'
          component={FormTextField}
          label='Kolejność'
          type='number'
          inputProps={{ min: 0, step: 1 }}
        />
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <Field
            name='validFrom'
            component={FormDateTimeField}
            label='Ważna od'
            style={{ flex: 1, minWidth: 0 }}
          />
          <Typography component='span' aria-hidden='true' style={{ margin: '0 16px' }}>
            -
          </Typography>
          <Field
            name='validTo'
            component={FormDateTimeField}
            label='Ważna do'
            style={{ flex: 1, minWidth: 0 }}
          />
        </div>
        <Field
          name='tenantScope'
          component={FormSelect}
          label='Operatorzy'
          multiple
          fullWidth
          values={tenantOptions(tenants, initialValues.tenantScope)}
          readonly={busy}
        />
        <FormHelperText>
          Wymagani aktywni operatorzy z ważną licencją. Uprawnienia są
          weryfikowane przy zapisie.
        </FormHelperText>
        <Field
          name='placements'
          component={FormSelect}
          label='Lokalizacje reklamy'
          multiple
          fullWidth
          values={placements}
          readonly={busy}
        />
        {showMobileStyleConfig && <div>
          <Typography style={{ marginTop: 16 }}>
            Aplikacja mobilna
          </Typography>
          <Field
            name='styles.mobile.width'
            component={FormTextField}
            label='Szerokość'
            type='number'
            inputProps={{ min: 1, step: 1 }}
          />
          <Field
            name='styles.mobile.height'
            component={FormTextField}
            label='Wysokość'
            type='number'
            inputProps={{ min: 1, step: 1 }}
          />
        </div>}
        {showWebStyleConfig && <div>
          <Typography style={{ marginTop: 16 }}>
            WWW
          </Typography>
          <Field
            name='styles.web.width'
            component={FormTextField}
            label='Szerokość'
            type='number'
            inputProps={{ min: 1, step: 1 }}
          />
          <Field
            name='styles.web.height'
            component={FormTextField}
            label='Wysokość'
            type='number'
            inputProps={{ min: 1, step: 1 }}
          />
        </div>}
        <Field
          name='mainImage'
          component={FormImageField}
          label='Obraz główny'
          previewAlt='Podgląd reklamy'
          currentImage={currentImage}
        />
      </div>
    </form>
  )
}

AdvertisementForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  tenants: PropTypes.array.isRequired,
  initialValues: PropTypes.object.isRequired,
  currentImage: PropTypes.string,
  selectedPlacements: PropTypes.arrayOf(PropTypes.string)
}

const selector = formValueSelector('advertisementForm')

export default reduxForm({
  form: 'advertisementForm',
  validate: validation
})(connect(state => ({
  selectedPlacements: selector(state, 'placements')
}))(AdvertisementForm))
